import { AdministrationService } from '../administration/administration.service';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { UserType } from '../../types';
import { GlobalDataService } from '../global-data/global-data.service';
import { HqSessionRecoveryService } from './hq-session-recovery.service';
import { AdvisorSessionRecoveryService } from './advisor-session-recovery.service';
import { SamSessionService } from './sam-session.service';

export const advisorSessionInterceptor: HttpInterceptorFn = (request, next) => {
  const global = inject(GlobalDataService);
  const recovery = inject(AdvisorSessionRecoveryService);
  const hqRecovery = inject(HqSessionRecoveryService);
  const samSession = inject(SamSessionService);
  const administration = inject(AdministrationService);
  const session = global.getSessionData().value;
  const generation = global.getAuthenticationGeneration();
  let url: URL;
  try { url = new URL(request.url, window.location.origin); }
  catch { return next(request); }
  const path = url.pathname.toLowerCase().replace(/\/+$/, '');
  if (url.origin !== window.location.origin || !path.startsWith('/api/') ||
      path === '/api/accounts' || path.startsWith('/api/accounts/')) return next(request);

  const unsafe = !['GET', 'HEAD', 'OPTIONS'].includes(request.method.toUpperCase());
  const isAuthenticate = request.method === 'POST' && path === '/api/user/authenticate';
  const isSessionRead = request.method === 'GET' && path === '/api/user/session';
  const isSignOut = request.method === 'POST' && path === '/api/user/signout';
  const headers = isAuthenticate ? request.headers : request.headers.delete('Authorization');
  const transported = request.clone({
    withCredentials: true,
    headers: session.initialized && !isAuthenticate && !isSessionRead
      ? (unsafe
        ? headers.set('X-SAM-Session', session.sessionVersion ?? '').set('X-SAM-CSRF', session.csrfToken ?? '')
        : headers.set('X-SAM-Session', session.sessionVersion ?? ''))
      : headers,
  });
  if (unsafe && !isAuthenticate && !session.initialized) {
    return throwError(() => new Error('SAM session is not initialized; write blocked'));
  }
  return next(transported).pipe(catchError(error => {
    const current = global.getSessionData().value;
    if (!isAuthenticate && !isSessionRead && !isSignOut && error instanceof HttpErrorResponse &&
        session.initialized && current.initialized && session.sessionVersion &&
        current.sessionVersion === session.sessionVersion && global.isAuthenticationCurrent(generation)) {
      if (error.status === 401 && error.error?.code === 'sam_session_expired') {
        if (session.userType === UserType.AgillicAdvisor) recovery.beginRecovery(session.sessionVersion);
        if (session.userType === UserType.ActiveDirectory) hqRecovery.beginRecovery(session.sessionVersion);
      } else if (error.status === 401 || error.status === 409 && error.error?.code === 'sam_session_changed') {
        void samSession.refresh();
      }
    }
    if (!isAuthenticate && !isSessionRead && !isSignOut && path !== '/api/administration/me' &&
        error instanceof HttpErrorResponse && error.status === 403 &&
        session.initialized && current.initialized && current.sessionVersion === session.sessionVersion &&
        global.isAuthenticationCurrent(generation)) {
      administration.getMyRoles().subscribe({ error: () => {} });
    }
    // Never replay a request: a write may already have reached the server.
    return throwError(() => error);
  }));
};
